import './chat.css';
import type { ChatLine, DmLine, SharedShot } from '../net/protocol';
import { t, onLang } from './i18n';
import { parseChatInput } from './chatCommands';

/**
 * The in-game chat overlay: a message log that sits transparently over the table
 * and a one-line input that only appears while you're actually typing.
 *
 *   • Enter          → open the input (the panel dims in to 15% opaque)
 *   • Enter again    → send, and the panel goes back to fully transparent
 *   • Escape         → close without sending
 *   • Tab            → cycle conversations
 *
 * Conversations are tabs. The first tab is the ROOM: available in the waiting
 * room and during a game (see `setRoomChat`). Every other tab is a private
 * conversation (귓속말) opened with `/w <nickname>`, and those work anywhere —
 * the App routes them over the always-on presence socket, so you can DM from the
 * menus just as well as mid-shot.
 *
 * The App owns both sockets; this only renders lines and reports what was typed.
 */
export interface ChatCallbacks {
  /** Send to everyone in the room (only reachable while the room tab is enabled). */
  onSend(text: string): void;
  /** Send a private message to `to`. */
  onWhisper(to: string, text: string): void;
  /** Play a shot someone shared into the room. */
  onReplayShot(shot: SharedShot): void;
}

interface Entry {
  name: string;
  text: string;
  ts: number;
  system?: boolean;
  mine?: boolean;
  /** An attached shot the room can replay (rendered as a card under the text). */
  shot?: SharedShot;
}

interface Tab {
  id: string;
  /** Nickname this conversation is with; null for the room/system tab. */
  peer: string | null;
  entries: Entry[];
  unread: number;
}

const ROOM_TAB = 'room';
/** While idle (not typing), only messages from the last stretch stay on screen. */
const IDLE_MS = 45000;
const IDLE_LINES = 6;
/** Per-conversation scrollback cap, so a long session can't grow without bound. */
const MAX_ENTRIES = 200;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class ChatPanel {
  private readonly panel = el('div');
  private readonly tabsEl = el('div', 'chat-tabs');
  private readonly log = el('div', 'chat-log');
  private readonly form = el('div', 'chat-form interactive');
  private readonly to = el('span', 'chat-to');
  private readonly input = el('input', 'chat-input') as HTMLInputElement;
  private readonly opener = el('button', 'chat-opener interactive');

  /** tabs[0] is always the room/system tab; the rest are private conversations. */
  private readonly tabs: Tab[] = [{ id: ROOM_TAB, peer: null, entries: [], unread: 0 }];
  private activeId = ROOM_TAB;
  /** Is room chat available right now? (waiting room / live game — not the menus.) */
  private roomChat = false;
  /** The input is open: the panel is dimmed in and keystrokes belong to it. */
  private open = false;
  /** Last private conversation touched — the target of `/r`. */
  private lastPeer = '';
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set when the next render should stick the log to the bottom. */
  private scrollPending = false;

  constructor(root: HTMLElement, private readonly cb: ChatCallbacks) {
    this.panel.id = 'chat-overlay';

    this.opener.textContent = '💬';
    this.opener.onclick = () => (this.open ? this.close() : this.openInput());

    this.input.type = 'text';
    this.input.maxLength = 300;
    this.input.autocomplete = 'off';
    this.input.onkeydown = (e) => {
      // gameplay keys must never fire while composing a message
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        this.cycleTab(e.shiftKey ? -1 : 1);
      }
    };
    this.input.onkeyup = (e) => e.stopPropagation();
    this.input.onkeypress = (e) => e.stopPropagation();

    this.form.append(this.to, this.input);
    this.panel.append(this.tabsEl, this.log, this.form, this.opener);

    // clicking away (e.g. back onto the table) closes an untouched input, but
    // never discards a half-typed message
    this.panel.addEventListener('focusout', (e) => {
      const next = e.relatedTarget as Node | null;
      if (this.open && !this.input.value.trim() && (!next || !this.panel.contains(next))) this.close();
    });

    onLang(() => this.render());
    root.appendChild(this.panel);
    this.render();
  }

  // ---- state from the App -------------------------------------------------

  /** Room chat on (waiting room / in a game) or off (menus, room browser). */
  setRoomChat(on: boolean): void {
    if (this.roomChat === on) return;
    this.roomChat = on;
    if (!on) {
      this.tabs[0].entries.length = 0; // the room's backlog doesn't outlive the room
      this.tabs[0].unread = 0;
      if (this.activeId === ROOM_TAB) this.scrollPending = true;
    }
    this.render();
  }

  // ---- opening / closing --------------------------------------------------

  /** Is the input open? (The App suppresses gameplay keys while it is.) */
  isActive(): boolean {
    return this.open;
  }

  /** Pop the input open (Enter). `prefill` seeds the line, e.g. "/w Min-jun ". */
  openInput(prefill?: string): void {
    this.open = true;
    if (prefill !== undefined) this.input.value = prefill;
    this.activeTab().unread = 0;
    this.scrollPending = true;
    this.render();
    this.input.focus();
    // put the caret at the end of a prefilled line
    const n = this.input.value.length;
    this.input.setSelectionRange(n, n);
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.input.value = '';
    this.input.blur();
    this.render();
  }

  /** Open (or create) a private conversation with `name` and start typing to it. */
  openDm(name: string): void {
    const tab = this.ensureDm(name);
    this.activeId = tab.id;
    this.lastPeer = tab.peer!;
    this.openInput('');
  }

  // ---- incoming lines -----------------------------------------------------

  /** Append one room chat line (player message or a system notice). */
  addLine(line: ChatLine, mine: boolean): void {
    this.push(ROOM_TAB, {
      name: line.name,
      text: line.text,
      ts: line.ts || Date.now(),
      system: line.system,
      mine,
      shot: line.shot,
    });
  }

  /** Append a private message — ours (the server's echo) or someone else's. */
  addDm(dm: DmLine): void {
    const peer = dm.echo ? dm.to : dm.from;
    const tab = this.ensureDm(peer);
    this.lastPeer = tab.peer!;
    this.push(tab.id, { name: dm.echo ? dm.from : peer, text: dm.text, ts: dm.ts || Date.now(), mine: !!dm.echo });
  }

  /** A local system notice in the room/system tab (not sent over the wire). */
  system(text: string): void {
    this.push(ROOM_TAB, { name: '', text, ts: Date.now(), system: true });
  }

  /** A system notice inside the conversation with `peer` (e.g. "they're offline"). */
  systemDm(peer: string, text: string): void {
    this.push(this.ensureDm(peer).id, { name: '', text, ts: Date.now(), system: true });
  }

  clear(): void {
    for (const tab of this.tabs) {
      tab.entries.length = 0;
      tab.unread = 0;
    }
    this.render();
  }

  // ---- internals ----------------------------------------------------------

  private activeTab(): Tab {
    return this.tabs.find((tb) => tb.id === this.activeId) ?? this.tabs[0];
  }

  private tabLabel(tab: Tab): string {
    if (tab.peer) return tab.peer;
    return this.roomChat ? t('chat.tabRoom') : t('chat.tabWhisper');
  }

  private ensureDm(name: string): Tab {
    const peer = name.trim();
    const id = `dm:${peer.toLowerCase()}`;
    const found = this.tabs.find((tb) => tb.id === id);
    if (found) return found;
    const tab: Tab = { id, peer, entries: [], unread: 0 };
    this.tabs.push(tab);
    return tab;
  }

  private push(tabId: string, entry: Entry): void {
    const tab = this.tabs.find((tb) => tb.id === tabId);
    if (!tab) return;
    tab.entries.push(entry);
    if (tab.entries.length > MAX_ENTRIES) tab.entries.splice(0, tab.entries.length - MAX_ENTRIES);
    if (tab.id !== this.activeId) tab.unread++;
    else this.scrollPending = true;
    this.render();
  }

  private setActiveTab(id: string): void {
    this.activeId = id;
    this.activeTab().unread = 0;
    this.scrollPending = true;
    this.render();
    if (this.open) this.input.focus();
  }

  private cycleTab(dir: number): void {
    const i = this.tabs.findIndex((tb) => tb.id === this.activeId);
    const n = this.tabs.length;
    this.setActiveTab(this.tabs[(((i + dir) % n) + n) % n].id);
  }

  /** Send whatever is typed, interpreting any leading slash command. */
  private submit(): void {
    const cmd = parseChatInput(this.input.value);
    this.input.value = '';
    if (!cmd) {
      this.close();
      return;
    }
    switch (cmd.kind) {
      case 'say': {
        const tab = this.activeTab();
        if (tab.peer) this.sendWhisper(tab.peer, cmd.text);
        else if (this.roomChat) this.cb.onSend(cmd.text);
        else {
          // outside a room there's nobody to broadcast to — only whispers work
          this.push(ROOM_TAB, { name: '', text: t('chat.noRoom'), ts: Date.now(), system: true });
          return; // keep the input open so the hint can be acted on straight away
        }
        this.close();
        break;
      }
      case 'whisper': {
        const tab = this.ensureDm(cmd.to);
        this.setActiveTab(tab.id);
        this.lastPeer = tab.peer!;
        if (!cmd.text) return; // "/w name" alone: just switch, and keep typing
        this.sendWhisper(tab.peer!, cmd.text);
        this.close();
        break;
      }
      case 'reply': {
        if (!this.lastPeer) {
          this.pushHint(t('chat.noReply'));
          return;
        }
        const tab = this.ensureDm(this.lastPeer);
        this.setActiveTab(tab.id);
        if (!cmd.text) return;
        this.sendWhisper(tab.peer!, cmd.text);
        this.close();
        break;
      }
      case 'help':
        this.pushHint(t('chat.help'));
        return; // usage shown — leave the input open
      case 'unknown':
        this.pushHint(t('chat.unknown', { name: cmd.name }));
        return;
    }
  }

  /** The message itself is rendered when the server echoes it back, so a line
   *  only ever appears once it has actually left the client. */
  private sendWhisper(to: string, text: string): void {
    this.lastPeer = to;
    this.cb.onWhisper(to, text);
  }

  private pushHint(text: string): void {
    this.push(this.activeId, { name: '', text, ts: Date.now(), system: true });
  }

  // ---- rendering ----------------------------------------------------------

  private render(): void {
    const tab = this.activeTab();
    const idle = !this.open;

    this.panel.className = this.open ? 'chat-open interactive' : '';
    // `visibility`, not `display`: the input row keeps its space when hidden, so
    // opening it never nudges the messages above it
    this.form.style.visibility = this.open ? 'visible' : 'hidden';
    this.to.textContent = tab.peer ? t('chat.toPeer', { name: tab.peer }) : this.tabLabel(tab);
    this.to.className = `chat-to${tab.peer ? ' dm' : ''}`;
    this.input.placeholder = t(tab.peer ? 'chat.placeholderDm' : this.roomChat ? 'chat.placeholder' : 'chat.placeholderWhisper');
    this.opener.title = t('chat.openHint');

    this.renderTabs();

    // Idle keeps only the recent tail on screen so the table stays readable;
    // while typing you get the whole (scrollable) conversation.
    const cutoff = Date.now() - IDLE_MS;
    const shown = idle ? tab.entries.filter((e) => e.ts >= cutoff).slice(-IDLE_LINES) : tab.entries;
    this.log.replaceChildren(...shown.map((e) => this.renderEntry(e, tab)));

    if (this.scrollPending) {
      this.scrollPending = false;
      this.log.scrollTop = this.log.scrollHeight;
    }
    this.scheduleIdleFade(idle, tab);
  }

  private renderTabs(): void {
    // The strip is noise while there's only the one conversation — but it stays
    // laid out (hidden, not removed) so showing it can't shift the log below it.
    this.tabsEl.style.visibility = this.open || this.tabs.length > 1 ? 'visible' : 'hidden';
    this.tabsEl.className = 'chat-tabs interactive';
    this.tabsEl.replaceChildren(
      ...this.tabs.map((tb) => {
        const b = el('button', `chat-tab${tb.id === this.activeId ? ' active' : ''}${tb.unread ? ' unread' : ''}`);
        b.textContent = this.tabLabel(tb) + (tb.unread ? ` (${tb.unread})` : '');
        b.onclick = () => this.setActiveTab(tb.id);
        return b;
      })
    );
  }

  private renderEntry(e: Entry, tab: Tab): HTMLElement {
    const row = el('div', `chat-line${e.system ? ' system' : ''}${e.mine ? ' mine' : ''}${tab.peer ? ' dm' : ''}`);
    if (e.system) {
      row.textContent = e.text;
      return row;
    }
    const who = el('span', 'chat-who');
    // in a private conversation, who said it is the useful bit ("→ them" / "them")
    who.textContent = tab.peer ? (e.mine ? `→ ${tab.peer}` : tab.peer) : e.name;
    const body = el('span', 'chat-text');
    body.textContent = e.text;
    row.append(who, body);
    if (e.shot) row.appendChild(this.renderShotCard(e.shot));
    return row;
  }

  /**
   * A shared shot, as a card you can play. The engine is deterministic, so the
   * layout + cue that crossed the wire is enough to reproduce the shot exactly
   * on this table — no video, no upload.
   */
  private renderShotCard(shot: SharedShot): HTMLElement {
    const card = el('button', 'chat-shot');
    const icon = el('span', 'chat-shot__icon');
    icon.textContent = '🎱';
    const meta = el('span', 'chat-shot__meta');
    const title = el('span', 'chat-shot__title');
    title.textContent = t('share.cardTitle');
    const sub = el('span', 'chat-shot__sub');
    sub.textContent = shot.label || t(`variant.${shot.variant}`);
    meta.append(title, sub);
    const play = el('span', 'chat-shot__play');
    play.textContent = `▶ ${t('share.replay')}`;
    card.append(icon, meta, play);
    card.onclick = () => this.cb.onReplayShot(shot);
    return card;
  }

  /** While idle, re-render exactly when the oldest visible line ages out. */
  private scheduleIdleFade(idle: boolean, tab: Tab): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (!idle) return;
    const cutoff = Date.now() - IDLE_MS;
    const next = tab.entries.find((e) => e.ts >= cutoff);
    if (!next) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.render();
    }, Math.max(250, next.ts - cutoff));
  }
}
